// Canonical Apple Music track links, resolved via the server's iTunes proxy.
//
// music.apple.com search URLs only work in a plain browser: on iOS/macOS the
// OS routes them to the native Music app as universal links, and the app has
// no handler for the search route — it opens on its home screen and the
// search is lost. Canonical song URLs deep-link reliably in both the app and
// the web player, so tracklists resolve them in the background (see
// useAppleMusicSongLinks) and swap them into the anchors' hrefs. Clicks stay
// plain anchor navigations — no intermediate tab for the OS to leave behind
// when the native app takes over.

import { API_BASE } from './config';
import { fetchWithRetry } from './authClient';

// key -> url | null. A null is a definitive catalog miss (cached so we stop
// asking); transient failures (rate-limit queue full, upstream errors) are
// NOT cached so a later render can retry.
const resolvedUrls = new Map();
const inFlight = new Map();

export function trackLinkKey(track) {
  return `${(track?.artistName || '').toLowerCase()}\n${(track?.name || '').toLowerCase()}`;
}

export function resolveAppleMusicSongUrl(track) {
  const artist = track?.artistName || '';
  const name = track?.name || '';
  if (!artist || !name) return Promise.resolve(null);

  const key = trackLinkKey(track);
  if (resolvedUrls.has(key)) return Promise.resolve(resolvedUrls.get(key));

  const existing = inFlight.get(key);
  if (existing) return existing;

  const params = new URLSearchParams({ artist, track: name });
  const promise = fetchWithRetry(`${API_BASE}/api/itunes/song-link?${params.toString()}`)
    .then(async (res) => {
      if (!res.ok) return null; // transient — don't cache
      const data = await res.json();
      const url = data.url || null;
      resolvedUrls.set(key, url);
      return url;
    })
    .catch(() => null)
    .finally(() => inFlight.delete(key));

  inFlight.set(key, promise);
  return promise;
}
